import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import EmailTemplatesSection from "./EmailTemplatesSection";
import ReportPromptsCard from "./ReportPromptsCard";
import GlossaryCard from "./GlossaryCard";
import RichTextEditor from "./RichTextEditor";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Mail, KeyRound, PlugZap, Save, Trash2, ShieldCheck } from "lucide-react";
import { API } from "../App";

const KEY_PROVIDERS = [
  { id: "anthropic", label: "Anthropic (Claude)", placeholder: "sk-ant-..." },
  { id: "openai", label: "OpenAI (GPT)", placeholder: "sk-..." },
  { id: "google", label: "Google (Gemini) — optionnel si Vertex AI est actif", placeholder: "AIza..." },
  { id: "calendly", label: "Calendly (jeton personnel)", placeholder: "eyJ..." },
];

const SmtpCard = () => {
  const [form, setForm] = useState({ host: "", port: 587, username: "", password: "", from_email: "", from_name: "", use_tls: true, signature: "" });
  const [configured, setConfigured] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    axios.get(`${API}/settings/smtp`, { withCredentials: true }).then((r) => {
      setConfigured(r.data.configured);
      setForm((f) => ({ ...f, ...r.data, password: "" }));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy("save");
    try {
      const { configured: _c, ...payload } = form;
      await axios.put(`${API}/settings/smtp`, payload, { withCredentials: true });
      setConfigured(true); setForm((f) => ({ ...f, password: "" }));
      toast.success("Paramètres SMTP enregistrés");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(""); }
  };

  const test = async () => {
    setBusy("test");
    try {
      const r = await axios.post(`${API}/settings/smtp/test`, {}, { withCredentials: true });
      toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Connexion impossible"); }
    finally { setBusy(""); }
  };

  const f = (k) => ({ value: form[k] ?? "", onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  return (
    <Card data-testid="smtp-card">
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2"><Mail className="w-5 h-5" /> Envoi d'e-mails (SMTP)
          {configured && <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid="smtp-configured-badge">Configuré</span>}
        </CardTitle>
        <p className="text-sm text-muted-foreground">Les e-mails de suivi partent depuis votre propre boîte (Gmail, OVH, Outlook...). Le mot de passe est chiffré au repos.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2"><Label>Serveur SMTP</Label><Input {...f("host")} placeholder="smtp.gmail.com" data-testid="smtp-host-input" /></div>
          <div><Label>Port</Label><Input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: Number(e.target.value) })} data-testid="smtp-port-input" /></div>
          <div><Label>Identifiant</Label><Input {...f("username")} placeholder="vous@domaine.fr" data-testid="smtp-username-input" /></div>
          <div><Label>Mot de passe {configured && <span className="text-muted-foreground text-xs">(laisser vide pour conserver)</span>}</Label><Input type="password" {...f("password")} placeholder="••••••••" data-testid="smtp-password-input" /></div>
          <div className="flex items-end gap-2 pb-2"><Switch checked={form.use_tls} onCheckedChange={(v) => setForm({ ...form, use_tls: v })} data-testid="smtp-tls-switch" /><Label>STARTTLS (port 587)</Label></div>
          <div><Label>E-mail expéditeur</Label><Input type="email" {...f("from_email")} placeholder="vous@domaine.fr" data-testid="smtp-from-email-input" /></div>
          <div className="sm:col-span-2"><Label>Nom expéditeur</Label><Input {...f("from_name")} placeholder="Dr Prénom Nom" data-testid="smtp-from-name-input" /></div>
        </div>
        <div><Label>Signature (ajoutée à chaque e-mail)</Label><RichTextEditor value={form.signature} onChange={(v) => setForm({ ...form, signature: v })} placeholder="Cordialement, ..." /></div>
        <div className="flex justify-end gap-2 flex-wrap">
          <Button variant="outline" onClick={test} disabled={busy !== "" || !configured} className="gap-2" data-testid="test-smtp-btn"><PlugZap className="w-4 h-4" /> {busy === "test" ? "Test..." : "Tester la connexion"}</Button>
          <Button onClick={save} disabled={busy !== "" || !form.host || !form.from_email} className="gap-2" data-testid="save-smtp-btn"><Save className="w-4 h-4" /> {busy === "save" ? "Enregistrement..." : "Enregistrer"}</Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ApiKeysCard = () => {
  const [keys, setKeys] = useState({});
  const [inputs, setInputs] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { axios.get(`${API}/settings/keys`, { withCredentials: true }).then((r) => setKeys(r.data)).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(inputs).filter(([, v]) => v?.trim()).map(([k, v]) => [`${k}_api_key`, v.trim()]));
      const r = await axios.put(`${API}/settings/keys`, payload, { withCredentials: true });
      setKeys(r.data); setInputs({}); toast.success("Clés enregistrées (chiffrées)");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { const r = await axios.delete(`${API}/settings/keys/${id}`, { withCredentials: true }); setKeys(r.data); toast.success("Clé supprimée"); }
    catch { toast.error("Erreur"); }
  };

  return (
    <Card data-testid="api-keys-card">
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2"><KeyRound className="w-5 h-5" /> Clés API personnelles</CardTitle>
        <p className="text-sm text-muted-foreground flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Vos clés sont chiffrées et utilisées en priorité pour la génération IA (conformité HDS). À défaut, la clé de la plateforme est utilisée.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className={`rounded-lg border p-3 text-sm flex items-start gap-3 ${keys.vertex?.configured ? "border-emerald-300 bg-emerald-50/60 dark:bg-emerald-950/20" : "border-dashed"}`} data-testid="vertex-status">
          <ShieldCheck className={`w-5 h-5 shrink-0 ${keys.vertex?.configured ? "text-emerald-600" : "text-muted-foreground"}`} />
          <div className="min-w-0">
            <p className="font-medium">Gemini via Vertex AI (UE) {keys.vertex?.configured ? <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-600 text-white align-middle">Actif</span> : <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground align-middle">Non configuré</span>}</p>
            <p className="text-xs text-muted-foreground">Configuré côté serveur (compte de service GCP), aucune clé à saisir, données conservées en Europe — idéal HDS.{keys.vertex?.configured && <> Projet : {keys.vertex.project} · Région : {keys.vertex.location}</>}</p>
          </div>
        </div>
        {KEY_PROVIDERS.map((p) => (
          <div key={p.id} className="grid sm:grid-cols-[180px_1fr_auto] gap-2 items-center" data-testid={`key-row-${p.id}`}>
            <Label className="flex flex-col"><span>{p.label}</span>
              {keys[p.id]?.configured && <span className="text-xs text-emerald-600 font-mono" data-testid={`key-hint-${p.id}`}>{keys[p.id].hint}</span>}</Label>
            <Input type="password" value={inputs[p.id] || ""} onChange={(e) => setInputs({ ...inputs, [p.id]: e.target.value })} placeholder={keys[p.id]?.configured ? "Remplacer la clé..." : p.placeholder} data-testid={`key-input-${p.id}`} />
            <Button variant="ghost" size="sm" className="text-destructive" disabled={!keys[p.id]?.configured} onClick={() => remove(p.id)} data-testid={`key-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
        <div className="flex justify-end"><Button onClick={save} disabled={saving || !Object.values(inputs).some((v) => v?.trim())} className="gap-2" data-testid="save-keys-btn"><Save className="w-4 h-4" /> {saving ? "Enregistrement..." : "Enregistrer les clés"}</Button></div>
      </CardContent>
    </Card>
  );
};

const MessagingSettings = () => (
  <div id="messaging" className="space-y-6 scroll-mt-6" data-testid="messaging-settings-section">
    <div className="flex items-center gap-2 pt-2">
      <Mail className="w-5 h-5 text-primary" />
      <h2 className="font-heading text-lg md:text-xl font-semibold">Messagerie &amp; IA</h2>
    </div>
    <p className="text-sm text-muted-foreground -mt-4">Envoi des e-mails de suivi post-séance, modèles, consignes IA des comptes rendus, clés IA et jeton Calendly.</p>
    <SmtpCard />
    <EmailTemplatesSection />
    <ReportPromptsCard />
    <GlossaryCard />
    <ApiKeysCard />
  </div>
);

export default MessagingSettings;
